import { useEffect, useMemo, useState } from "react";

export const CPF_PAGE_SIZES = [25, 50, 100, 250] as const;

export type CpfPageSize = (typeof CPF_PAGE_SIZES)[number];

const DEFAULT_PAGE_SIZE: CpfPageSize = 50;

interface CpfPagination {
  page: number;
  pageSize: CpfPageSize;
  pageCount: number;
  pageItems: string[];
  firstItemIndex: number;
  lastItemIndex: number;
  setPage: (page: number) => void;
  setPageSize: (size: CpfPageSize) => void;
}

export function useCpfPagination(items: string[]): CpfPagination {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<CpfPageSize>(DEFAULT_PAGE_SIZE);

  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;

  const pageItems = useMemo(
    () => items.slice(start, start + pageSize),
    [items, start, pageSize],
  );

  return {
    page: safePage,
    pageSize,
    pageCount,
    pageItems,
    firstItemIndex: items.length === 0 ? 0 : start + 1,
    lastItemIndex: start + pageItems.length,
    setPage,
    setPageSize,
  };
}
